/**
 * Enhanced Firestore Service
 * Uses API backend instead of direct Firestore calls
 * This replaces the old firestore.ts service with API-first approach
 */

import { userApi, workoutApi, ApiError } from './api';
import { UserProfile, WorkoutSession, WeeklyProgress } from '@/types/workout';

/**
 * Enhanced Firestore Service using API backend
 */
export class FirestoreService {
  /**
   * User Profile Operations
   */
  async createUserProfile(userId: string, profile: Omit<UserProfile, 'uid'>): Promise<void> {
    await userApi.createProfile({
      name: profile.name,
      sex: profile.sex,
      competitionDate: profile.competitionDate instanceof Date ? profile.competitionDate.toISOString() : (profile.competitionDate || null),
      startDate: profile.startDate instanceof Date ? profile.startDate.toISOString() : profile.startDate,
      preferences: profile.preferences,
      healthProfile: profile.healthProfile,
      currentMaxes: profile.currentMaxes,
    });
  }

  async getUserProfile(_userId: string): Promise<UserProfile | null> {
    try {
      const profile = await userApi.getProfile();
      if (!profile) return null;

      // Convert date strings back to Date objects
      return {
        ...profile,
        startDate: profile.startDate ? new Date(profile.startDate) : undefined,
        competitionDate: profile.competitionDate ? new Date(profile.competitionDate) : undefined,
        createdAt: profile.createdAt ? new Date(profile.createdAt) : new Date(),
        updatedAt: profile.updatedAt ? new Date(profile.updatedAt) : new Date(),
      };
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null;
      }

      throw error;
    }
  }

  async updateUserProfile(_userId: string, updates: Partial<UserProfile>): Promise<void> {
    await userApi.updateProfile({
      name: updates.name,
      sex: updates.sex,
      competitionDate: updates.competitionDate instanceof Date ? updates.competitionDate.toISOString() : (updates.competitionDate || null),
      startDate: updates.startDate instanceof Date ? updates.startDate.toISOString() : updates.startDate,
      preferences: updates.preferences,
      healthProfile: updates.healthProfile,
      currentMaxes: updates.currentMaxes,
    });
  }

  /**
   * Workout Session Operations
   */
  async saveWorkoutSession(userId: string, session: WorkoutSession): Promise<void> {
    if (session.id) {
      // Update existing session
      await workoutApi.updateSession(session.id, {
        exerciseLogs: session.exerciseLogs,
        notes: session.notes,
        status: session.status === 'in_progress' ? 'in-progress' : session.status,
        completedAt: session.completedAt?.toISOString(),
      });
    } else {
      // Create new session
      await workoutApi.createSession({
        week: session.week,
        day: session.day,
        session: session.session,
        exercises: session.exercises,
        date: session.date.toISOString(),
        notes: session.notes,
      });
    }
  }

  async getWorkoutSession(sessionId: string): Promise<WorkoutSession | null> {
    try {
      return await workoutApi.getSession(sessionId);
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null;
      }

      throw error;
    }
  }

  async getUserWorkoutSessions(userId: string, week?: number): Promise<WorkoutSession[]> {
    return await workoutApi.getUserSessions({ week });
  }

  async getWorkoutSessionsByWeek(userId: string, week: number): Promise<WorkoutSession[]> {
    return await workoutApi.getWeekSessions(week);
  }

  /**
   * Weekly Progress Operations
   */
  async saveWeeklyProgress(_userId: string, _progress: WeeklyProgress): Promise<void> {
    // This would need to be implemented in the API
    // For now, we'll use the workout sessions to calculate progress
    // TODO: Implement weekly progress saving
  }

  async getWeeklyProgress(userId: string, week: number): Promise<WeeklyProgress | null> {
    // This would need to be implemented in the API
    // For now, we'll calculate from workout sessions
    const sessions = await this.getWorkoutSessionsByWeek(userId, week);

    if (sessions.length === 0) return null;

    const completedSessions = sessions.filter(s => s.status === 'completed').length;
    const totalSessions = sessions.length;

    return {
      userId,
      week,
      phase: Math.ceil(week / 6), // Rough phase calculation
      completedSessions,
      totalSessions,
      completionRate: (completedSessions / totalSessions) * 100,
      totalVolume: 0, // Would need to calculate from exercise logs
      averageRPE: 0, // Would need to calculate from exercise logs
      updatedAt: new Date(),
    };
  }

  /**
   * Real-time listeners (fallback to polling for API)
   * Note: These will be replaced with WebSocket connections in the future
   */
  subscribeToUserProfile(
    userId: string,
    callback: (_profile: UserProfile | null) => void
  ): () => void {

    let isSubscribed = true;
    let retryCount = 0;
    const baseDelay = 15000; // 15 seconds base delay
    const maxDelay = 120000; // 2 minutes max delay

    const poll = async () => {
      if (!isSubscribed) return;

      try {

        const profile = await this.getUserProfile(userId);

        callback(profile);
        retryCount = 0; // Reset retry count on success
      } catch (error) {

        retryCount++;
      }

      if (isSubscribed) {
        // Exponential backoff for failed requests
        const delay = retryCount > 0
          ? Math.min(baseDelay * Math.pow(2, retryCount - 1), maxDelay)
          : baseDelay;

        setTimeout(poll, delay);
      }
    };

    // Initial fetch

    poll();

    // Return unsubscribe function
    return () => {

      isSubscribed = false;
    };
  }

  subscribeToWorkoutSessions(
    userId: string,
    week: number,
    callback: (_sessions: WorkoutSession[]) => void
  ): () => void {
    let isSubscribed = true;
    
    const poll = async () => {
      if (!isSubscribed) return;
      
      try {
        const sessions = await this.getWorkoutSessionsByWeek(userId, week);
        callback(sessions);
      } catch (error) {
        // Error polling workout sessions - continuing to poll
        // Continue polling despite errors
      }
      
      if (isSubscribed) {
        setTimeout(poll, 30000); // Poll every 30 seconds (reduced from 10 seconds)
      }
    };

    // Initial fetch
    poll();

    // Return unsubscribe function
    return () => {
      isSubscribed = false;
    };
  }

  /**
   * Utility methods
   */
  async checkApiConnectivity(): Promise<boolean> {
    try {
      // Use the health endpoint which doesn't require authentication
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/health`);
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  async getApiStatus(): Promise<{
    connected: boolean;
    latency?: number;
    error?: string;
  }> {
    const startTime = Date.now();

    try {
      const connected = await this.checkApiConnectivity();
      const latency = Date.now() - startTime;

      return {
        connected,
        latency,
      };
    } catch (error: any) {
      return {
        connected: false,
        error: error.message || 'Unknown error',
      };
    }
  }
}

// Create singleton instance
export const firestoreService = new FirestoreService();

// Export legacy functions for backward compatibility
export const createUserProfile = (userId: string, profile: Omit<UserProfile, 'uid'>) => 
  firestoreService.createUserProfile(userId, profile);

export const getUserProfile = (userId: string) => 
  firestoreService.getUserProfile(userId);

export const updateUserProfile = (userId: string, updates: Partial<UserProfile>) => 
  firestoreService.updateUserProfile(userId, updates);

export const saveWorkoutSession = (userId: string, session: WorkoutSession) => 
  firestoreService.saveWorkoutSession(userId, session);

export const getWorkoutSession = (sessionId: string) => 
  firestoreService.getWorkoutSession(sessionId);

export const getUserWorkoutSessions = (userId: string, week?: number) => 
  firestoreService.getUserWorkoutSessions(userId, week);

export const getWorkoutSessionsByWeek = (userId: string, week: number) => 
  firestoreService.getWorkoutSessionsByWeek(userId, week);

export const subscribeToUserProfile = (userId: string, callback: (_profile: UserProfile | null) => void) =>
  firestoreService.subscribeToUserProfile(userId, callback);

export const subscribeToWorkoutSessions = (userId: string, week: number, callback: (_sessions: WorkoutSession[]) => void) =>
  firestoreService.subscribeToWorkoutSessions(userId, week, callback);
